import { useEffect, useRef } from "react";
import { Trans } from "react-i18next";
import classNames from "classnames";
import { Events } from "xgplayer";
import { IconAttention } from "@icons/index";
import { useVideoPlayer } from "@hooks/videoplayer";

interface DetailVideoPlayerProps {
  videoURL: string;
  aspectRatio?: string;
  suspended?: boolean;
  /** 播放器 DOM/实例 id，双播放器场景需各自唯一。默认 detail-videoplayer 保持单播放器行为不变。 */
  playerId?: string;
  /** 是否展示底部提示文案，复刻双播放器场景可关闭。 */
  showHint?: boolean;
  /** 是否进入即自动播放。默认 true，保持单播放器旧行为不变。 */
  autoplay?: boolean;
  /** 该值变化时触发播放（用于「同时播放」）。 */
  playCommand?: number;
  /** 该值变化时触发暂停（用于「同时暂停」）。 */
  pauseCommand?: number;
  /** 播放/暂停状态变化时回调，用于外部聚合多个播放器状态。 */
  onPlayingChange?: (playing: boolean) => void;
}

export const DetailVideoPlayer = ({
  videoURL,
  aspectRatio,
  suspended = false,
  playerId = "detail-videoplayer",
  showHint = true,
  autoplay = true,
  playCommand,
  pauseCommand,
  onPlayingChange,
}: DetailVideoPlayerProps) => {
  const isHorizontal = aspectRatio === "RATIO_16_9" || aspectRatio === "16:9";
  const shouldResumeAfterSuspendRef = useRef(false);
  const suspendedRef = useRef(suspended);
  const onPlayingChangeRef = useRef(onPlayingChange);
  onPlayingChangeRef.current = onPlayingChange;
  const pendingPlayRef = useRef(false);

  const { refPlayer } = useVideoPlayer(
    {
      id: playerId,
      url: videoURL,
      autoplay,
      ignores: ["cssFullScreen", "playbackrate"],
      download: false,
      fluid: true,
    },
    [
      { eventName: Events.PLAY, handle: () => onPlayingChangeRef.current?.(true) },
      { eventName: Events.PLAYING, handle: () => onPlayingChangeRef.current?.(true) },
      { eventName: Events.PAUSE, handle: () => onPlayingChangeRef.current?.(false) },
      { eventName: Events.ENDED, handle: () => onPlayingChangeRef.current?.(false) },
      {
        eventName: Events.CANPLAY,
        handle: () => {
          if (!pendingPlayRef.current) return;
          pendingPlayRef.current = false;
          const player = refPlayer.current;
          if (player) Promise.resolve(player.play()).catch(() => {});
        },
      },
    ],
    { kalo_pos: "video_detail", kalo_type: "detail" },
  );

  useEffect(() => {
    if (playCommand === undefined) return;
    const player = refPlayer.current;
    if (!player) return;
    if (player.readyState >= 3) {
      Promise.resolve(player.play()).catch(() => {});
    } else {
      pendingPlayRef.current = true;
    }
  }, [playCommand, refPlayer]);

  useEffect(() => {
    if (pauseCommand === undefined) return;
    const player = refPlayer.current;
    if (!player) return;
    pendingPlayRef.current = false;
    player.pause();
  }, [pauseCommand, refPlayer]);

  useEffect(() => {
    if (suspendedRef.current === suspended) return;

    const player = refPlayer.current;
    suspendedRef.current = suspended;

    if (!player) return;

    if (suspended) {
      shouldResumeAfterSuspendRef.current = !player.paused;
      player.pause();
      return;
    }

    if (shouldResumeAfterSuspendRef.current) {
      shouldResumeAfterSuspendRef.current = false;
      Promise.resolve(player.play()).catch(() => {});
    }
  }, [refPlayer, suspended]);

  return (
    <div className="w-full h-full flex flex-col items-center gap-[22px] pt-[47px]">
      <div className="flex-1 min-h-0 w-full flex items-center justify-center overflow-hidden">
        <div
          className={classNames(
            "overflow-hidden bg-black detail-video-container",
            isHorizontal ? "w-full aspect-video max-h-full" : "h-full aspect-9/16 max-w-full",
          )}
          data-detail-player-suspended={suspended ? "true" : "false"}
        >
          <div id={playerId} />
        </div>
      </div>
      {showHint && (
        <div className="shrink-0 flex items-center gap-[4px] text-kds-fg-subtle text-[12px] font-normal">
          <IconAttention className="w-[20px] h-[20px] **:stroke-current" />
          <Trans>Kaloclip AI may make mistakes, so double-check your videos.</Trans>
        </div>
      )}
    </div>
  );
};
